import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppStore } from '@app/stores/appStore';

/**
 * Central coordination point that tells automatic refresh timers
 * (generic-table auto-reload, events polling) to pause while the user is
 * actively working — an open dialog, an active inline edit, or the carrier
 * tour-plan mode would otherwise lose state when the table reloads.
 *
 * This blocks ONLY automatic timer-based refreshes. The intentional refresh
 * bus (searchStore.triggerDataRefresh) and the manual refresh button must
 * never consult this service: action services deliberately refresh after a
 * successful save, sometimes while their dialog is still open.
 */
@Injectable({ providedIn: 'root' })
export class AutoRefreshBlockerService {
    private dialog = inject(MatDialog);
    private appStore = inject(AppStore);

    /** reason -> refcount for manually acquired blockers */
    private readonly manualBlockers = signal<ReadonlyMap<string, number>>(
        new Map()
    );
    /**
     * Dialogs seen opened and not yet closed.
     *
     * Deliberately NOT derived from MatDialog.openDialogs.length: MatDialog
     * registers the subscriber that splices a ref out of that array AFTER it
     * emits afterOpened, so our own afterClosed subscriber -- registered from
     * within afterOpened -- runs first and would still see the closing dialog
     * in the array. Reading the length there never drops back to 0, which used
     * to leave every auto-reload timer paused for the rest of the session.
     */
    private readonly openDialogRefs = signal<
        ReadonlySet<MatDialogRef<unknown>>
    >(new Set());

    readonly hasOpenDialogs = computed(() => this.openDialogRefs().size > 0);
    readonly isTourPlanModeActive = computed(() =>
        this.appStore.isTourPlanMode()
    );
    // Upload mode only CSS-hides the consignor table (consignor-overview),
    // so its timer would keep reloading behind the upload panel.
    readonly isUploadModeActive = computed(() => this.appStore.isUploadMode());

    readonly isBlocked = computed(
        () =>
            this.hasOpenDialogs() ||
            this.isTourPlanModeActive() ||
            this.isUploadModeActive() ||
            this.manualBlockers().size > 0
    );

    /** Active block sources, for tooltips/debugging. */
    readonly blockReasons = computed<string[]>(() => {
        const reasons: string[] = [];
        if (this.hasOpenDialogs()) {
            reasons.push('dialog-open');
        }
        if (this.isTourPlanModeActive()) {
            reasons.push('tour-plan-mode');
        }
        if (this.isUploadModeActive()) {
            reasons.push('upload-mode');
        }
        reasons.push(...this.manualBlockers().keys());
        return reasons;
    });

    constructor() {
        // The root MatDialog instance sees dialogs opened through child
        // injectors as well, so one subscription covers every dialog site
        // without touching the individual open() calls.
        this.dialog.afterOpened
            .pipe(takeUntilDestroyed())
            .subscribe(ref => this.trackDialog(ref));

        // Self-healing net: a stuck blocker would silently kill every refresh
        // timer for the rest of the session, so trust MatDialog when it reports
        // that nothing is open any more.
        this.dialog.afterAllClosed
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.openDialogRefs.set(new Set()));

        // This service is created lazily (first inject); a dialog may
        // already be open at that point and would otherwise go uncounted.
        for (const ref of this.dialog.openDialogs) {
            this.trackDialog(ref);
        }
    }

    /**
     * Registers a manual blocker (e.g. an active inline edit). Returns a
     * release function; releasing more than once is a no-op.
     */
    acquire(reason: string): () => void {
        this.updateRefcount(reason, 1);
        let released = false;
        return () => {
            if (released) {
                return;
            }
            released = true;
            this.updateRefcount(reason, -1);
        };
    }

    /** afterClosed emits once and completes, so the subscription self-ends. */
    private trackDialog(ref: MatDialogRef<unknown>): void {
        this.openDialogRefs.update(current => new Set(current).add(ref));
        ref.afterClosed().subscribe(() => this.untrackDialog(ref));
    }

    private untrackDialog(ref: MatDialogRef<unknown>): void {
        this.openDialogRefs.update(current => {
            if (!current.has(ref)) {
                return current;
            }
            const next = new Set(current);
            next.delete(ref);
            return next;
        });
    }

    private updateRefcount(reason: string, delta: number): void {
        this.manualBlockers.update(current => {
            const next = new Map(current);
            const count = (next.get(reason) ?? 0) + delta;
            if (count > 0) {
                next.set(reason, count);
            } else {
                next.delete(reason);
            }
            return next;
        });
    }
}
